import { Box, Button, createFilterOptions, Typography, useTheme } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { Chip } from 'src/components/common/Chip'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { z } from 'zod'
import { useGetCustomerUsers } from '../../../../api/customer'
import { AppForm, FormSelectField, FormSubmitButton, FormTextField } from '../../../../components/FormCore'
import { IUser } from '../../../../generated-types'
import { useZodForm } from '../../../../hooks/zod-form'
import { EdgeCase, getUserDetail, UserDetail } from '../../../../utils/getUserDetails'

const ResourceFrom = ({ initialValues, customerId, onSubmit, pop, isUpdateForm = false, isLoading = false }) => {
  const { t: translate } = useTranslation(['locality', 'common'])
  const theme = useTheme()
  const [selectedOption, setSelectedOption] = useState(initialValues?.user)

  const schema = z.object({
    name: z
      .string()
      .min(1, translate('nameIsRequired', { ns: 'common' }))
      .max(200),
    description: z
      .string()
      .min(1, translate('descriptionIsRequired', { ns: 'common' }))
      .max(400),
    userId: z.string().optional().nullable(),
    email: z.string().optional().nullable(),
  })

  const form = useZodForm(schema, { defaultValues: initialValues })

  const { data: customerUsers } = useGetCustomerUsers(customerId as EntityId)

  const handleSubmit = async form => {
    if (isUpdateForm) {
      form.id = initialValues.id
    }
    // Validate email if it exists
    if (form.email) {
      try {
        z.string().email().parse(form.email)
      } catch (error) {
        toast.error(translate('invalid_email', { ns: 'access-control' }))
        return
      }
    }
    await onSubmit(form, pop)
  }
  const filter = createFilterOptions()

  const extractEmail = str => {
    const emailRegex = /\b[A-Za-z0-9._%+()-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g
    const match = str.match(emailRegex)
    return match ? match[0] : null
  }

  return (
    <AppForm form={form} onSubmit={handleSubmit} hasInitialValues={isUpdateForm}>
      <FormTextField name="name" label={translate('name', { ns: 'common' })} defaultValue={initialValues?.Name} />
      <FormTextField name="description" label={translate('description', { ns: 'common' })} multiline rows={4} />
      <FormSelectField
        name="responsibleUser"
        data={(customerUsers! as IUser[]) ?? []}
        label={translate('responsibleUser', { ns: 'locality' })}
        getOptionLabel={option => {
          const isExisting = customerUsers?.some(user => user.email.toLowerCase() === option.email.toLowerCase())
          if (!isExisting) return option.email
          return getUserDetail(option, UserDetail.NameAndEmail, EdgeCase.ReturnEmailForNewUser)
        }}
        renderOption={(props: any, option) => {
          return (
            <Box {...props} key={option.id || option.inputValue}>
              <Typography>
                {option.isNew
                  ? option.label
                  : getUserDetail(option, UserDetail.NameAndEmail, EdgeCase.ReturnEmailForNewUser)}
              </Typography>
              {option.isNew && (
                <Chip
                  label={translate('newUser', { ns: 'task' })}
                  size="small"
                  sx={{
                    ml: 1,
                    backgroundColor: getThemeColor(theme, MUIThemeColors.primaryLight),
                    color: getThemeColor(theme, MUIThemeColors.white),
                    height: '20px',
                    fontSize: '12px',
                  }}
                />
              )}
            </Box>
          )
        }}
        value={selectedOption}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        onChange={(e, value) => {
          setSelectedOption(value)
          form.setValue('userId', value ? (value as IUser).id : null)
        }}
        onInputChange={(e, value) => {
          const email = extractEmail(value) ?? value
          const existingUser = customerUsers?.filter(option => option.email.toLowerCase() === email.toLowerCase())
          if (!existingUser || existingUser.length === 0) {
            form.setValue('email', email)
          } else {
            form.setValue('email', undefined)
          }
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params)
          const { inputValue } = params
          const email = extractEmail(inputValue) ?? inputValue
          const isExisting = options.some(option => option.email.toLowerCase() === email.toLowerCase())
          if (email && !isExisting) {
            filtered.push({
              label: `${email}`,
              email: email,
              isNew: true,
            })
          }

          return filtered
        }}
      />
      <Box display={'flex'} flexDirection={'row'} justifyContent={'end'}>
        <FormSubmitButton createText={translate('add', { ns: 'common' })} isLoading={isLoading} />
        <Button key="locality-form-cancel" onClick={pop}>
          {translate('cancel', { ns: 'common' })}
        </Button>
      </Box>
    </AppForm>
  )
}

export default ResourceFrom
